/**
 * What to do with a row that changed underneath the view.
 *
 * A realtime event, or somebody else's edit, can move a row into the view, out
 * of it, or to a different place within it. Deciding which of those happened is
 * the same question for every view type, so it is answered here, once, as pure
 * data: the caller is told what to do and does it to its own buffer.
 *
 * Kept apart from the buffer deliberately. The grid, a board's stacks and a
 * gallery all hold rows differently, but they all need this decision, and it is
 * far easier to test as a returned instruction than as a mutation.
 */
import { getRowSortFunction, type OrderTerm } from './rowSort'
import { rowMatchesFilters, type FilterableView } from './rowMatch'
import type { FilterContext } from '../filters'
import type { Field, RowData } from '@/api/types'

/** Where a row belongs among the rows it is compared against. */
export interface RowPosition {
  /** The row it should sit after, or null when it sorts first. */
  anchorRowId: number | null
  /** Its index in the sorted comparison set. */
  sortedIndex: number
  isFirst: boolean
  isLast: boolean
}

/**
 * Whether a row still belongs where it is.
 *
 * `matchFilters` false means the row no longer belongs in the view at all.
 * `matchSortings` false means it belongs, but not at the index it currently
 * occupies -- which is what the grid shows as a warning badge rather than
 * moving the row, so that a cell being edited does not jump away mid-keystroke.
 */
export interface RowMatchFlags {
  matchFilters: boolean
  matchSortings: boolean
}

/** Everything the decision needs that is not the row itself. */
export interface RowLifecycleContext {
  view: FilterableView & { sortings?: readonly OrderTerm[] }
  fields: readonly Field[]
  groupBys?: readonly OrderTerm[]
  filterContext?: FilterContext
  /**
   * The rows this one is ordered against.
   *
   * Only a window of the table in practice, which is why `matchSortings` is
   * answered relative to the comparison set rather than to the whole table.
   */
  rowsForComparison: (row: RowData) => readonly RowData[]
}

/** What the caller should do with the row. */
export type RowAction =
  | { type: 'none' }
  | { type: 'remove'; rowId: number }
  | { type: 'insert'; row: RowData; position: RowPosition; flags: RowMatchFlags }
  | {
      type: 'replace'
      rowId: number
      row: RowData
      position: RowPosition
      flags: RowMatchFlags
    }

function sortFunctionFor(context: RowLifecycleContext) {
  return getRowSortFunction(
    context.view.sortings ?? [],
    context.fields,
    context.groupBys ?? [],
  )
}

/**
 * Where a row lands among the others once sorted.
 *
 * The row is added to the comparison set rather than searched for in it, so
 * this answers for a row that is not in the view yet as readily as for one that
 * is.
 */
export function computeRowInsertPosition(
  row: RowData,
  existingRows: readonly RowData[],
  context: RowLifecycleContext,
): RowPosition {
  const sorted = [...existingRows, row].sort(sortFunctionFor(context))
  const sortedIndex = sorted.findIndex((r) => r.id === row.id)
  return {
    anchorRowId: sortedIndex > 0 ? (sorted[sortedIndex - 1]?.id ?? null) : null,
    sortedIndex,
    isFirst: sortedIndex === 0,
    isLast: sortedIndex === sorted.length - 1,
  }
}

/**
 * Whether a row still matches the view's filters, and still sorts where it sits.
 *
 * The sort check replaces the row in its own comparison set before sorting, so
 * it is the *edited* row whose position is tested. A row not in the comparison
 * set is reported as correctly sorted: there is nothing to compare it against,
 * and claiming otherwise would badge every row outside the loaded window.
 */
export function computeRowMatchFlags(
  row: RowData,
  context: RowLifecycleContext,
  rowsInSortingGroup: readonly RowData[] = context.rowsForComparison(row),
): RowMatchFlags {
  const matchFilters = rowMatchesFilters(
    row,
    context.view,
    context.fields,
    context.filterContext,
  )

  let matchSortings = true
  if (context.fields.length > 0) {
    const currentIndex = rowsInSortingGroup.findIndex((r) => r.id === row.id)
    if (currentIndex >= 0) {
      const rows = [...rowsInSortingGroup]
      rows[currentIndex] = { ...rows[currentIndex], ...row }
      const sorted = rows.sort(sortFunctionFor(context))
      matchSortings = sorted.findIndex((r) => r.id === row.id) === currentIndex
    }
  }

  return { matchFilters, matchSortings }
}

/** A row was created elsewhere: insert it only if it belongs in this view. */
export function rowCreated(row: RowData, context: RowLifecycleContext): RowAction {
  const existing = context.rowsForComparison(row)
  const flags = computeRowMatchFlags(row, context, existing)
  if (!flags.matchFilters) return { type: 'none' }

  return {
    type: 'insert',
    row,
    position: computeRowInsertPosition(row, existing, context),
    flags,
  }
}

/**
 * A row was deleted elsewhere: remove it only if it was in this view.
 *
 * Checking first matters because the buffer holds a window, not the table: a
 * row that never matched the filters is not in it, and removing by id anyway
 * would shift the window's count for a row it never counted.
 */
export function rowDeleted(row: RowData, context: RowLifecycleContext): RowAction {
  const flags = computeRowMatchFlags(row, context)
  if (!flags.matchFilters) return { type: 'none' }
  return { type: 'remove', rowId: row.id }
}

/**
 * A row that is on screen: does it still belong there, and where?
 *
 * The question the *acting* tab asks, and it is not `rowUpdated`'s. That one
 * compares a row before and after a change it is being told about; this one is
 * asked about a row whose new values are already in the window -- because this
 * tab put them there optimistically, or because the server has just answered
 * with what it made of them. There is no "before" left to compare with, and
 * asking for one would mean keeping a copy of every row an edit has touched
 * until its request lands.
 *
 * Never `none`: the row is in the view, so "it does not belong" is a removal
 * rather than nothing to do.
 */
export function rowSettled(row: RowData, context: RowLifecycleContext): RowAction {
  // Judged against a set that excludes itself, so it is not compared with its
  // own position when deciding where it now belongs.
  const others = context.rowsForComparison(row).filter((r) => r.id !== row.id)
  const flags = computeRowMatchFlags(row, context, others)
  if (!flags.matchFilters) return { type: 'remove', rowId: row.id }

  return {
    type: 'replace',
    rowId: row.id,
    row,
    position: computeRowInsertPosition(row, others, context),
    flags,
  }
}

/**
 * A row was updated elsewhere.
 *
 * Four cases, decided by whether the row matched the view before and after:
 *
 * | before | after | action  |
 * |--------|-------|---------|
 * | yes    | no    | remove  |
 * | no     | yes   | insert  |
 * | no     | no    | nothing |
 * | yes    | yes   | replace |
 */
export function rowUpdated(
  oldRow: RowData,
  newRow: RowData,
  context: RowLifecycleContext,
): RowAction {
  const wasInView = computeRowMatchFlags(oldRow, context).matchFilters

  // The new row is judged against a set that excludes itself, so it is not
  // compared with its own stale position when deciding where it now belongs.
  const others = context.rowsForComparison(newRow).filter((r) => r.id !== newRow.id)
  const flags = computeRowMatchFlags(newRow, context, others)
  const isInView = flags.matchFilters

  if (wasInView && !isInView) return { type: 'remove', rowId: newRow.id }
  if (!wasInView && !isInView) return { type: 'none' }

  const position = computeRowInsertPosition(newRow, others, context)
  return wasInView
    ? { type: 'replace', rowId: newRow.id, row: newRow, position, flags }
    : { type: 'insert', row: newRow, position, flags }
}
